/**
 * Main Phantasy Live2D SDK Class
 * Integrates Live2D models with TTS and lip sync functionality
 */

import { SDKEventEmitter } from './EventEmitter';
import { TTSManager } from '../tts/TTSManager';
import { ModelManager } from '../live2d/ModelManager';
import { LipSyncEngine } from '../lipSync/LipSyncEngine';
import { ConfigManager } from './ConfigManager';
import { createLogger } from '../utils/logger';

import type { PhantasyLive2DConfig, SDKDefaults } from '../types/config';
import type { Live2DModel } from '../types/live2d';

export class PhantasyLive2D extends SDKEventEmitter {
  private static defaults: SDKDefaults = {};

  private config: Required<PhantasyLive2DConfig>;
  private configManager: ConfigManager;
  private ttsManager: TTSManager;
  private modelManager: ModelManager;
  private lipSyncEngine: LipSyncEngine;
  private logger: ReturnType<typeof createLogger>;

  private container: HTMLElement;
  private isInitialized = false;
  private isDestroyed = false;

  constructor(config: PhantasyLive2DConfig) {
    super();

    this.logger = createLogger('PhantasyLive2D');

    // DEBUG: Log incoming config
    this.logger.info('PhantasyLive2D constructor received config:', {
      hasModel: !!config.model,
      modelType: typeof config.model,
      modelConfig: config.model,
    });

    this.configManager = new ConfigManager(PhantasyLive2D.defaults);
    this.config = this.configManager.mergeConfig(config);

    // Resolve container element
    this.container = this.resolveContainer(this.config.container);

    // Initialize managers
    this.ttsManager = new TTSManager(this.config.tts);
    const modelConfig =
      typeof this.config.model === 'string'
        ? {
            path: this.config.model,
            scale: 0.3,
            position: { x: 0.5, y: 0.5 },
            expressions: {},
          }
        : this.config.model;
    this.modelManager = new ModelManager(modelConfig, this.config.features);
    this.lipSyncEngine = new LipSyncEngine(this.config.lipSync);

    // Set up event forwarding
    this.setupEventForwarding();

    this.logger.info('Phantasy Live2D SDK initialized with config:', this.config);
  }

  /**
   * Set global default configuration
   */
  static setDefaults(defaults: SDKDefaults): void {
    PhantasyLive2D.defaults = { ...PhantasyLive2D.defaults, ...defaults };
  }

  /**
   * Initialize the SDK - loads model and sets up all systems
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      this.logger.warn('SDK already initialized');
      return;
    }

    if (this.isDestroyed) {
      throw new Error('Cannot initialize destroyed SDK instance');
    }

    try {
      this.logger.info('Initializing SDK...');

      // Initialize model manager first
      await this.modelManager.initialize(this.container);
      const model = this.modelManager.getModel();

      if (!model) {
        throw new Error('Failed to initialize Live2D model');
      }

      // Initialize TTS manager
      await this.ttsManager.initialize();

      // Initialize lip sync engine with the model ONLY if enabled
      if (this.config.lipSync.enabled) {
        this.logger.info('LipSync enabled - initializing LipSyncEngine');
        await this.lipSyncEngine.initialize(model);
        // Connect TTS to lip sync only if lip sync is enabled
        this.connectTTSToLipSync();
      } else {
        this.logger.info('LipSync disabled - skipping LipSyncEngine initialization');
      }

      this.isInitialized = true;
      this.emit('initialized');

      if (this.config.onReady) {
        this.config.onReady();
      }

      this.logger.info('SDK initialization complete');
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('SDK initialization failed:', err);
      this.emit('error', err, 'initialization');

      if (this.config.onError) {
        this.config.onError(err);
      }

      throw err;
    }
  }

  /**
   * Speak text using TTS with lip sync
   */
  async speak(text: string): Promise<void> {
    this.ensureInitialized();

    try {
      this.logger.info('Speaking text:', text);
      this.emit('speechStarted', text);

      // Generate and play audio with lip sync
      const audioBlob = await this.ttsManager.generateAudio(text);
      await this.ttsManager.playAudio(audioBlob);

      this.emit('speechEnded', text);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('Speech failed:', err);
      this.emit('speechError', err, text);
      throw err;
    }
  }

  /**
   * Set model expression
   */
  setExpression(expression: string): void {
    this.ensureInitialized();
    this.modelManager.setExpression(expression);
    this.emit('expressionChanged', expression);
  }

  /**
   * Get current model instance
   */
  getModel(): Live2DModel | null {
    return this.modelManager.getModel();
  }

  /**
   * Update SDK configuration
   */
  updateConfig(newConfig: Partial<PhantasyLive2DConfig>): void {
    const updated = this.configManager.updateConfig(newConfig);

    if (updated.tts) {
      this.ttsManager.updateConfig(updated.tts);
    }

    if (updated.lipSync) {
      this.lipSyncEngine.updateConfig(updated.lipSync);
    }

    if (updated.model || updated.features) {
      // Model config changes require re-initialization
      this.logger.warn('Model configuration changes require re-initialization');
    }
  }

  /**
   * Stop current speech
   */
  stopSpeech(): void {
    if (this.isInitialized) {
      this.ttsManager.stop();
      this.lipSyncEngine.reset();
    }
  }

  // Orbit Controls API

  /**
   * Enable orbit controls for zoom and pan functionality
   */
  enableOrbitControls(): void {
    this.ensureInitialized();
    this.modelManager.enableOrbitControls();
  }

  /**
   * Disable orbit controls
   */
  disableOrbitControls(): void {
    this.ensureInitialized();
    this.modelManager.disableOrbitControls();
  }

  /**
   * Set zoom level
   * @param zoom - Zoom factor (0.1 to 3.0)
   */
  setZoom(zoom: number): void {
    this.ensureInitialized();
    this.modelManager.setZoom(zoom);
  }

  /**
   * Set pan position
   * @param x - Horizontal pan offset
   * @param y - Vertical pan offset
   */
  setPan(x: number, y: number): void {
    this.ensureInitialized();
    this.modelManager.setPan(x, y);
  }

  /**
   * Reset orbit controls to default position and zoom
   */
  resetOrbitControls(): void {
    this.ensureInitialized();
    this.modelManager.resetOrbitControls();
  }

  /**
   * Get current orbit controls state
   * @returns Object with zoom, panX, and panY values, or null if not available
   */
  getOrbitControlsState(): { zoom: number; panX: number; panY: number } | null {
    if (!this.isInitialized) return null;
    return this.modelManager.getOrbitControlsState();
  }

  /**
   * Destroy SDK instance and clean up resources
   */
  destroy(): void {
    if (this.isDestroyed) {
      this.logger.warn('SDK already destroyed');
      return;
    }

    this.logger.info('Destroying SDK...');

    try {
      // Stop any ongoing operations
      this.stopSpeech();

      // Clean up managers
      this.lipSyncEngine.destroy();
      this.modelManager.destroy();
      this.ttsManager.destroy();

      // Clear all event listeners
      this.removeAllListeners();

      this.isInitialized = false;
      this.isDestroyed = true;

      this.emit('destroyed');
      this.logger.info('SDK destroyed successfully');
    } catch (error) {
      this.logger.error('Error during SDK destruction:', error);
    }
  }

  /**
   * Get SDK status
   */
  getStatus() {
    return {
      initialized: this.isInitialized,
      destroyed: this.isDestroyed,
      hasModel: !!this.modelManager.getModel(),
      ttsReady: (this.ttsManager as any).isReady(),
      lipSyncActive: (this.lipSyncEngine as any).isActive(),
    };
  }

  // Private methods

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const element = document.querySelector(container);
      if (!element) {
        throw new Error(`Container element not found: ${container}`);
      }
      return element as HTMLElement;
    }
    return container;
  }

  private ensureInitialized(): void {
    if (!this.isInitialized) {
      throw new Error('SDK not initialized. Call initialize() first.');
    }
    if (this.isDestroyed) {
      throw new Error('SDK has been destroyed');
    }
  }

  private setupEventForwarding(): void {
    // Forward events from managers
    this.modelManager.on('modelLoaded', model => this.emit('modelLoaded', model));
    this.modelManager.on('error', error => this.emit('error', error, 'model'));

    this.ttsManager.on('error', error => this.emit('error', error, 'tts'));

    this.lipSyncEngine.on('lipSyncData', volume => this.emit('lipSyncData', volume));
    this.lipSyncEngine.on('started', () => this.emit('lipSyncStarted'));
    this.lipSyncEngine.on('ended', () => this.emit('lipSyncEnded'));
    this.lipSyncEngine.on('error', error => this.emit('error', error, 'lipSync'));
  }

  private connectTTSToLipSync(): void {
    // Connect TTS audio analysis to lip sync engine
    this.ttsManager.setAudioDataCallback((volume: number) => {
      this.lipSyncEngine.processVolume(volume);
    });
  }
}
